import * as React from "react";
import './DistancePractice.css';

export interface Attempt {
  distance: number;
  trajectories: string[];
  attempts: number;
  timestamp: number;
  firstShotProximity?: number;
}

export interface DistanceBucketStats {
  startDistance: number;
  attemptsAverage: number;
  proximityAverage: number | null;
  roundCount: number;
  proximityCount: number;
}

export interface BucketDailyStats {
  date: string;
  attemptsAverage: number;
  proximityAverage: number | null;
  roundCount: number;
  proximityCount: number;
}

export interface DistancePracticeState {
  minDistance: number;
  maxDistance: number;
  selectedTrajectories: string[];
  currentDistance: number | null;
  attemptsInput: string;
  firstShotProximityInput: string;
  attemptHistory: Attempt[];
  showHistory: boolean;
  selectedDate: string | null;
  selectedBucket: number | null;
  chartStartDate: string;
  chartEndDate: string;
  view: 'practice' | 'chart';
}

export class DistancePractice extends React.Component<{}, DistancePracticeState> {
  public constructor(props) {
    super(props);
    this.state = {
      minDistance: 50,
      maxDistance: 200,
      selectedTrajectories: ['Low', 'Medium', 'High'],
      currentDistance: null,
      attemptsInput: '',
      firstShotProximityInput: '',
      attemptHistory: this.loadHistory(),
      showHistory: false,
      selectedDate: null,
      selectedBucket: null,
      chartStartDate: '',
      chartEndDate: '',
      view: 'practice',
    };
  }

  private loadHistory(): Attempt[] {
    const saved = localStorage.getItem('golfDistancePracticeHistory');
    return saved ? JSON.parse(saved) : [];
  }

  private saveHistory(history: Attempt[]) {
    localStorage.setItem('golfDistancePracticeHistory', JSON.stringify(history));
  }

  private randomizeDistance = () => {
    const { minDistance, maxDistance } = this.state;
    const newDistance = Math.floor(Math.random() * (maxDistance - minDistance + 1)) + minDistance;
    this.setState({
      currentDistance: newDistance,
      attemptsInput: '',
      firstShotProximityInput: '',
    });
  };

  private toggleTrajectory = (trajectory: string) => {
    const { selectedTrajectories } = this.state;
    const idx = (selectedTrajectories as string[]).indexOf(trajectory);
    const updated = idx >= 0
      ? selectedTrajectories.filter(t => t !== trajectory)
      : [...selectedTrajectories, trajectory];
    
    if (updated.length > 0) {
      this.setState({ selectedTrajectories: updated });
    }
  };

  private saveAttempt = () => {
    const { currentDistance, attemptsInput, firstShotProximityInput, selectedTrajectories, attemptHistory } = this.state;
    const attempts = parseInt(attemptsInput, 10);
    const firstShotProximity = firstShotProximityInput.trim() === ''
      ? null
      : parseFloat(firstShotProximityInput);

    if (!currentDistance || !attemptsInput || isNaN(attempts) || attempts < 1) {
      alert('Please enter a valid number of attempts');
      return;
    }

    if (firstShotProximityInput.trim() !== '' && (firstShotProximity === null || isNaN(firstShotProximity) || firstShotProximity < 0)) {
      alert('Please enter a valid first-shot proximity, or leave it blank');
      return;
    }

    const newAttempt: Attempt = {
      distance: currentDistance,
      trajectories: selectedTrajectories,
      attempts,
      timestamp: Date.now(),
      ...(firstShotProximity === null ? {} : { firstShotProximity }),
    };

    const updated = [...attemptHistory, newAttempt];
    this.setState({ attemptHistory: updated });
    this.saveHistory(updated);
    this.randomizeDistance();
  };

  private clearHistory = () => {
    if (window.confirm('Clear all practice history?')) {
      this.setState({ attemptHistory: [] });
      this.saveHistory([]);
    }
  };

  private getStats() {
    const { attemptHistory } = this.state;
    if (attemptHistory.length === 0) return null;

    const totalAttempts = attemptHistory.reduce((sum, a) => sum + a.attempts, 0);
    const avgAttempts = (totalAttempts / attemptHistory.length).toFixed(1);
    const bestAttempts = Math.min(...attemptHistory.map(a => a.attempts));

    // Calculate averages over time
    const last5 = attemptHistory.slice(-5);
    const avg5 = last5.length > 0 
      ? (last5.reduce((sum, a) => sum + a.attempts, 0) / last5.length).toFixed(1)
      : null;

    const last10 = attemptHistory.slice(-10);
    const avg10 = last10.length > 0 
      ? (last10.reduce((sum, a) => sum + a.attempts, 0) / last10.length).toFixed(1)
      : null;

    return { 
      totalRounds: attemptHistory.length, 
      avgAttempts, 
      bestAttempts,
      avg5,
      avg10
    };
  }

  private getFilteredAttempts() {
    const { attemptHistory, chartStartDate, chartEndDate } = this.state;
    if (chartStartDate && chartEndDate && chartStartDate > chartEndDate) return [];

    return attemptHistory.filter(attempt => {
      const date = new Date(attempt.timestamp).toISOString().split('T')[0];
      return (!chartStartDate || date >= chartStartDate) && (!chartEndDate || date <= chartEndDate);
    });
  }

  private getDistanceBucketStats(): DistanceBucketStats[] {
    const bucketMap: { [key: number]: { attemptsTotal: number; roundCount: number; proximityTotal: number; proximityCount: number } } = {};

    this.getFilteredAttempts().forEach(attempt => {
      const bucketStart = Math.floor(attempt.distance / 10) * 10;
      if (!bucketMap[bucketStart]) {
        bucketMap[bucketStart] = { attemptsTotal: 0, roundCount: 0, proximityTotal: 0, proximityCount: 0 };
      }

      const bucket = bucketMap[bucketStart];
      bucket.attemptsTotal += attempt.attempts;
      bucket.roundCount += 1;
      if (typeof attempt.firstShotProximity === 'number') {
        bucket.proximityTotal += attempt.firstShotProximity;
        bucket.proximityCount += 1;
      }
    });

    return Object.keys(bucketMap).map(Number).sort((a, b) => a - b).map(startDistance => {
      const bucket = bucketMap[startDistance];
      return {
        startDistance,
        attemptsAverage: bucket.attemptsTotal / bucket.roundCount,
        proximityAverage: bucket.proximityCount > 0 ? bucket.proximityTotal / bucket.proximityCount : null,
        roundCount: bucket.roundCount,
        proximityCount: bucket.proximityCount,
      };
    });
  }

  private getBucketDailyStats(startDistance: number): BucketDailyStats[] {
    const dailyMap: { [key: string]: { attemptsTotal: number; roundCount: number; proximityTotal: number; proximityCount: number } } = {};
    this.getFilteredAttempts()
      .filter(attempt => Math.floor(attempt.distance / 10) * 10 === startDistance)
      .forEach(attempt => {
        const date = new Date(attempt.timestamp).toISOString().split('T')[0];
        if (!dailyMap[date]) {
          dailyMap[date] = { attemptsTotal: 0, roundCount: 0, proximityTotal: 0, proximityCount: 0 };
        }
        const daily = dailyMap[date];
        daily.attemptsTotal += attempt.attempts;
        daily.roundCount += 1;
        if (typeof attempt.firstShotProximity === 'number') {
          daily.proximityTotal += attempt.firstShotProximity;
          daily.proximityCount += 1;
        }
      });

    return Object.keys(dailyMap).sort().map(date => {
      const daily = dailyMap[date];
      return {
        date,
        attemptsAverage: daily.attemptsTotal / daily.roundCount,
        proximityAverage: daily.proximityCount > 0 ? daily.proximityTotal / daily.proximityCount : null,
        roundCount: daily.roundCount,
        proximityCount: daily.proximityCount,
      };
    });
  }

  private renderBucketTrendMetric(points: Array<{ date: string; average: number }>, title: string, color: string, unit: string) {
    if (points.length === 0) {
      return (
        <div className="bucket-trend-empty">
          <h4>{title}</h4>
          <p>No first-shot proximity values recorded for this bucket in the selected dates.</p>
        </div>
      );
    }

    const width = 360;
    const height = 190;
    const padding = { top: 20, right: 18, bottom: 42, left: 38 };
    const graphWidth = width - padding.left - padding.right;
    const graphHeight = height - padding.top - padding.bottom;
    const values = points.map(point => point.average);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue || Math.max(1, maxValue * 0.2);
    const lowerBound = Math.max(0, minValue - range * 0.15);
    const upperBound = maxValue + range * 0.15;
    const yRange = upperBound - lowerBound || 1;
    const plottedPoints = points.map((point, index) => ({
      ...point,
      x: padding.left + (index / (points.length - 1 || 1)) * graphWidth,
      y: height - padding.bottom - ((point.average - lowerBound) / yRange) * graphHeight,
    }));
    const trendPoints = plottedPoints.map((point, index) => {
      const start = Math.max(0, index - 1);
      const end = Math.min(plottedPoints.length, index + 2);
      const window = plottedPoints.slice(start, end);
      const average = window.reduce((sum, entry) => sum + entry.average, 0) / window.length;
      return {
        x: point.x,
        y: height - padding.bottom - ((average - lowerBound) / yRange) * graphHeight,
      };
    });
    const dataPath = plottedPoints.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
    const trendPath = trendPoints.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');

    return (
      <div className="bucket-trend-metric">
        <h4>{title}</h4>
        <svg viewBox={`0 0 ${width} ${height}`} className="bucket-trend-chart" role="img" aria-label={`${title} over time`}>
          {[0, 0.5, 1].map((ratio, index) => {
            const y = height - padding.bottom - ratio * graphHeight;
            const value = lowerBound + ratio * yRange;
            return (
              <g key={`grid-${index}`}>
                <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e7ecef" />
                <text x={padding.left - 6} y={y + 4} textAnchor="end" className="bucket-axis-label">{value.toFixed(1)}</text>
              </g>
            );
          })}
          <path d={dataPath} fill="none" stroke={color} strokeWidth="2.5" />
          <path d={trendPath} fill="none" stroke="#34495e" strokeWidth="2" strokeDasharray="5 4" />
          {plottedPoints.map(point => (
            <g key={point.date}>
              <circle cx={point.x} cy={point.y} r="4" fill={color} />
              <title>{point.date}: {point.average.toFixed(1)} {unit}</title>
            </g>
          ))}
          {plottedPoints.map((point, index) => (
            index === 0 || index === plottedPoints.length - 1 || index % Math.ceil(plottedPoints.length / 4) === 0 ? (
              <text key={`date-${point.date}`} x={point.x} y={height - 14} textAnchor="middle" className="bucket-axis-label">{point.date.slice(5)}</text>
            ) : null
          ))}
        </svg>
        <div className="bucket-trend-legend">
          <span><i style={{ backgroundColor: color }} /> Daily average ({unit})</span>
          <span><i className="smoothed-legend" /> 3-point smoothed trend</span>
        </div>
      </div>
    );
  }

  private getDailyStats() {
    const attemptHistory = this.getFilteredAttempts();
    const dailyMap: { [key: string]: number[] } = {};

    attemptHistory.forEach(attempt => {
      const date = new Date(attempt.timestamp).toISOString().split('T')[0];
      if (!dailyMap[date]) {
        dailyMap[date] = [];
      }
      dailyMap[date].push(attempt.attempts);
    });

    const daily = Object.keys(dailyMap)
      .sort()
      .map(date => ({
        date,
        avg: parseFloat((dailyMap[date].reduce((a, b) => a + b, 0) / dailyMap[date].length).toFixed(2)),
        count: dailyMap[date].length,
      }));

    return daily;
  }

  private getSmoothTrendLine(daily: { date: string; avg: number }[], windowSize: number = 3) {
    return daily.map((item, idx) => {
      const start = Math.max(0, idx - Math.floor(windowSize / 2));
      const end = Math.min(daily.length, idx + Math.floor(windowSize / 2) + 1);
      const window = daily.slice(start, end);
      const avg = window.reduce((sum, d) => sum + d.avg, 0) / window.length;
      return parseFloat(avg.toFixed(2));
    });
  }

  private getChartData() {
    const daily = this.getDailyStats();
    if (daily.length === 0) return null;

    const trend = this.getSmoothTrendLine(daily);

    return {
      labels: daily.map(d => d.date),
      datasets: [
        {
          label: 'Daily Average Attempts',
          data: daily.map(d => d.avg),
          borderColor: '#27ae60',
          backgroundColor: 'rgba(39, 174, 96, 0.1)',
          tension: 0.3,
          fill: true,
          pointRadius: 4,
          pointBackgroundColor: '#27ae60',
        },
        {
          label: 'Trend (3-day avg)',
          data: trend,
          borderColor: '#3498db',
          borderDash: [5, 5],
          borderWidth: 2,
          fill: false,
          pointRadius: 0,
          tension: 0.3,
        },
      ],
    };
  }

  private deleteAttempt = (index: number) => {
    const { attemptHistory } = this.state;
    const updated = attemptHistory.filter((_, i) => i !== index);
    this.setState({ attemptHistory: updated });
    this.saveHistory(updated);
  };

  private getAttemptsForDate(date: string) {
    const { attemptHistory } = this.state;
    return attemptHistory
      .map((attempt, idx) => ({
        ...attempt,
        originalIndex: idx,
        dateStr: new Date(attempt.timestamp).toISOString().split('T')[0],
      }))
      .filter(a => a.dateStr === date);
  }

  private getTodaysAttempts() {
    const today = new Date().toISOString().split('T')[0];
    return this.getAttemptsForDate(today);
  }

  private renderLineChart() {
    const daily = this.getDailyStats();
    const trend = this.getSmoothTrendLine(daily);

    if (daily.length === 0) return null;

    const width = 300;
    const height = 250;
    const padding = 40;
    const graphWidth = width - padding * 2;
    const graphHeight = height - padding * 2;

    const maxValue = Math.max(...daily.map(d => d.avg), ...trend) + 1;
    const minValue = Math.min(...daily.map(d => d.avg), ...trend) - 1;
    const range = maxValue - minValue;

    // Generate points for actual data
    const points = daily.map((d, idx) => {
      const x = padding + (idx / (daily.length - 1 || 1)) * graphWidth;
      const y = height - padding - ((d.avg - minValue) / range) * graphHeight;
      return { x, y, date: d.date, idx };
    });

    // Generate points for trend line
    const trendPoints = trend.map((val, idx) => {
      const x = padding + (idx / (trend.length - 1 || 1)) * graphWidth;
      const y = height - padding - ((val - minValue) / range) * graphHeight;
      return { x, y };
    });

    // Create paths
    const dataPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const trendPath = trendPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return (
      <svg width={width} height={height} className="line-chart">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
          const y = height - padding - ratio * graphHeight;
          const val = minValue + ratio * range;
          return (
            <g key={i}>
              <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#eee" strokeWidth="1" />
              <text x={padding - 5} y={y + 4} fontSize="12" fill="#aaa" textAnchor="end">
                {Math.round(val)}
              </text>
            </g>
          );
        })}

        {/* Trend line */}
        <path d={trendPath} stroke="#3498db" strokeWidth="2" fill="none" strokeDasharray="5,5" />

        {/* Data line */}
        <path d={dataPath} stroke="#27ae60" strokeWidth="2" fill="none" />

        {/* Points and interactivity */}
        {points.map((p) => (
          <g
            key={p.idx}
            onClick={() => this.setState({ selectedDate: p.date })}
            style={{ cursor: 'pointer' }}
          >
            <circle cx={p.x} cy={p.y} r="5" fill="#27ae60" opacity="0.7" />
            <circle cx={p.x} cy={p.y} r="7" fill="none" stroke="#27ae60" strokeWidth="1" opacity="0" style={{ cursor: 'pointer' }} />
            <title>{p.date}</title>
          </g>
        ))}

        {/* Axes */}
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#333" strokeWidth="1" />
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#333" strokeWidth="1" />

        {/* X-axis labels (every nth date) */}
        {points.map((p, i) => {
          if (i % Math.ceil(daily.length / 4) === 0 || i === daily.length - 1) {
            return (
              <text key={`label-${i}`} x={p.x} y={height - padding + 20} fontSize="11" fill="#666" textAnchor="middle">
                {p.date.slice(5)}
              </text>
            );
          }
          return null;
        })}
      </svg>
    );
  }

  public render() {
    const { minDistance, maxDistance, selectedTrajectories, currentDistance, attemptsInput, firstShotProximityInput, attemptHistory, showHistory, view, selectedDate, selectedBucket, chartStartDate, chartEndDate } = this.state;
    const stats = this.getStats();
    const chartData = this.getChartData();
    const filteredAttempts = this.getFilteredAttempts();
    const distanceBuckets = this.getDistanceBucketStats();
    const maxBucketAttempts = Math.max(1, ...distanceBuckets.map(bucket => bucket.attemptsAverage));
    const maxBucketProximity = Math.max(1, ...distanceBuckets.map(bucket => bucket.proximityAverage || 0));

    return (
      <div className="distance-practice">
        {/* View Switcher */}
        <div className="view-switcher">
          <button 
            className={`view-btn ${view === 'practice' ? 'active' : ''}`}
            onClick={() => this.setState({ view: 'practice' })}
          >
            ⛳ Practice
          </button>
          <button 
            className={`view-btn ${view === 'chart' ? 'active' : ''}`}
            onClick={() => this.setState({ view: 'chart' })}
          >
            📊 Progress
          </button>
        </div>

        {view === 'practice' ? (
          <>
            {/* Settings Section */}
            <div className="distance-section settings-section">
              <h3>Distance Range</h3>
              
              <div className="range-inputs">
                <div className="input-group">
                  <label>Min: {minDistance}yd</label>
                  <input
                    type="range"
                    min="10"
                    max="290"
                    step="5"
                    value={minDistance}
                    onChange={(e) => {
                  const newMin = parseInt(e.target.value, 10);
                  if (newMin < maxDistance) {
                    this.setState({ minDistance: newMin });
                  }
                }}
                className="range-slider"
              />
            </div>

            <div className="input-group">
              <label>Max: {maxDistance}yd</label>
              <input
                type="range"
                min="20"
                max="300"
                step="5"
                value={maxDistance}
                onChange={(e) => {
                  const newMax = parseInt(e.target.value, 10);
                  if (newMax > minDistance) {
                    this.setState({ maxDistance: newMax });
                  }
                }}
                className="range-slider"
              />
            </div>
          </div>

          <h3 style={{ marginTop: '1.5rem' }}>Trajectory</h3>
          <div className="trajectory-buttons">
            {['Low', 'Medium', 'High'].map(trajectory => (
              <button
                key={trajectory}
                className={`trajectory-btn ${(selectedTrajectories as string[]).indexOf(trajectory) >= 0 ? 'active' : ''}`}
                onClick={() => this.toggleTrajectory(trajectory)}
              >
                {trajectory}
              </button>
            ))}
          </div>
        </div>

        {/* Practice Section */}
        <div className="distance-section practice-section">
          <h3>Your Target</h3>
          
          {currentDistance !== null ? (
            <>
              <div className="distance-display">
                <div className="distance-number">{currentDistance}</div>
                <div className="distance-unit">yards</div>
              </div>

              <div className="trajectory-display">
                Trajectory: {selectedTrajectories.join(' / ')}
              </div>

              <div className="input-group">
                <label>How many attempts?</label>
                <input
                  type="number"
                  min="1"
                  value={attemptsInput}
                  onChange={(e) => this.setState({ attemptsInput: e.target.value })}
                  placeholder="Enter attempts"
                  className="attempts-input"
                />
              </div>

              <div className="input-group first-shot-input-group">
                <label htmlFor="first-shot-proximity">First-shot proximity (optional)</label>
                <div className="distance-input-wrap">
                  <input
                    id="first-shot-proximity"
                    type="number"
                    min="0"
                    step="1"
                    value={firstShotProximityInput}
                    onChange={(e) => this.setState({ firstShotProximityInput: e.target.value })}
                    placeholder="Yards from target"
                    className="attempts-input"
                  />
                  <span>yd</span>
                </div>
                <small>Enter the yards remaining to the target after your first shot.</small>
              </div>

              <div className="button-group">
                <button className="btn-primary" onClick={this.saveAttempt}>
                  Save & Next Distance
                </button>
                <button className="btn-secondary" onClick={this.randomizeDistance}>
                  Randomize Again
                </button>
              </div>
            </>
          ) : (
            <div className="no-distance">
              <p>Ready to practice?</p>
              <button className="btn-primary btn-large" onClick={this.randomizeDistance}>
                Start Practice
              </button>
            </div>
          )}
        </div>

        {/* History Section */}
        {attemptHistory.length > 0 && (
          <div className="distance-section history-section">
            <div className="history-header">
              <h3>Your Stats</h3>
              <button 
                className="btn-toggle-history"
                onClick={() => this.setState({ showHistory: !showHistory })}
              >
                {showHistory ? '▼' : '▶'}
              </button>
            </div>

            {stats && (
              <div>
                <div className="stats">
                  <div className="stat-item">
                    <span className="stat-label">Rounds</span>
                    <span className="stat-value">{stats.totalRounds}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">All Time Avg</span>
                    <span className="stat-value">{stats.avgAttempts}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Best</span>
                    <span className="stat-value">{stats.bestAttempts}</span>
                  </div>
                </div>

                {(stats.avg10 || stats.avg5) && (
                  <div className="stats-progress">
                    {stats.avg10 && (
                      <div className="progress-item">
                        <span>Last 10 avg: <strong>{stats.avg10}</strong></span>
                      </div>
                    )}
                    {stats.avg5 && (
                      <div className="progress-item">
                        <span>Last 5 avg: <strong>{stats.avg5}</strong></span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {this.getTodaysAttempts().length > 0 && (
              <div className="distance-section todays-attempts-section">
                <h3>Today's Attempts</h3>
                <div className="todays-attempts-list">
                  {this.getTodaysAttempts().map((attempt, idx) => (
                    <div key={idx} className="todays-attempt-item">
                      <div className="attempt-info">
                        <div className="attempt-distance">{attempt.distance}yd</div>
                        <div className="attempt-details">
                          <div>{attempt.trajectories.join(' / ')}</div>
                          <div className="attempt-attempts">{attempt.attempts} attempt{attempt.attempts !== 1 ? 's' : ''}</div>
                          {typeof attempt.firstShotProximity === 'number' && (
                            <div className="first-shot-result">First-shot proximity: {attempt.firstShotProximity}yd</div>
                          )}
                        </div>
                      </div>
                      <button
                        className="btn-delete-todays"
                        onClick={() => this.deleteAttempt(attempt.originalIndex)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showHistory && (
              <div className="history-list">
                {attemptHistory.slice().reverse().map((attempt, idx) => (
                  <div key={idx} className="history-item">
                    <div className="history-distance">{attempt.distance}yd</div>
                    <div className="history-details">
                      <div>{attempt.trajectories.join(' / ')}</div>
                      <div className="history-attempts">{attempt.attempts} attempt{attempt.attempts !== 1 ? 's' : ''}</div>
                        {typeof attempt.firstShotProximity === 'number' && (
                          <div className="history-first-shot">First-shot proximity: {attempt.firstShotProximity}yd</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button className="btn-danger" onClick={this.clearHistory}>
              Clear History
            </button>
          </div>
        )}
          </>
        ) : (
          <>
            {/* Chart View */}
            {selectedDate ? (
              <div className="distance-section chart-detail-section">
                <button 
                  className="btn-back-to-chart"
                  onClick={() => this.setState({ selectedDate: null })}
                >
                  ← Back to Chart
                </button>
                <h3>Attempts on {selectedDate}</h3>
                <div className="date-attempts">
                  {this.getAttemptsForDate(selectedDate).map((attempt, idx) => (
                    <div key={idx} className="attempt-card">
                      <div className="attempt-info">
                        <div className="attempt-distance">{attempt.distance}yd</div>
                        <div className="attempt-details">
                          <div>{attempt.trajectories.join(' / ')}</div>
                          <div>{attempt.attempts} attempt{attempt.attempts !== 1 ? 's' : ''}</div>
                          {typeof attempt.firstShotProximity === 'number' && (
                            <div className="first-shot-result">First-shot proximity: {attempt.firstShotProximity}yd</div>
                          )}
                        </div>
                      </div>
                      <button
                        className="btn-delete-attempt"
                        onClick={() => this.deleteAttempt(attempt.originalIndex)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="distance-section chart-section">
                <h3>Progress Over Time</h3>
                {chartData ? (
                  <>
                    <div className="chart-container">
                      {this.renderLineChart()}
                    </div>
                    <p className="chart-hint">Click on a date point to see and manage attempts for that day</p>
                  </>
                ) : (
                  <p className="bucket-empty-state">No chart data for this date selection.</p>
                )}

                <div className="distance-bucket-section">
                  <div className="bucket-heading">
                    <h3>Distance Breakdown</h3>
                    <p>Compare average tries and first-shot proximity by 10-yard target range.</p>
                  </div>
                  <div className="date-range-controls">
                    <div className="input-group">
                      <label htmlFor="chart-start-date">From</label>
                      <input
                        id="chart-start-date"
                        type="date"
                        value={chartStartDate}
                        max={chartEndDate || undefined}
                        onChange={(e) => this.setState({ chartStartDate: e.target.value })}
                      />
                    </div>
                    <div className="input-group">
                      <label htmlFor="chart-end-date">To</label>
                      <input
                        id="chart-end-date"
                        type="date"
                        value={chartEndDate}
                        min={chartStartDate || undefined}
                        onChange={(e) => this.setState({ chartEndDate: e.target.value })}
                      />
                    </div>
                    {(chartStartDate || chartEndDate) && (
                      <button
                        className="btn-clear-dates"
                        onClick={() => this.setState({ chartStartDate: '', chartEndDate: '' })}
                      >
                        All dates
                      </button>
                    )}
                  </div>

                  {chartStartDate && chartEndDate && chartStartDate > chartEndDate ? (
                    <p className="bucket-empty-state">Choose a start date that is on or before the end date.</p>
                  ) : distanceBuckets.length === 0 ? (
                    <p className="bucket-empty-state">No rounds in this date selection yet.</p>
                  ) : (
                    <div className="bucket-list">
                      {distanceBuckets.map(bucket => (
                        <button
                          type="button"
                          className={`bucket-row ${selectedBucket === bucket.startDistance ? 'selected' : ''}`}
                          key={bucket.startDistance}
                          aria-expanded={selectedBucket === bucket.startDistance}
                          onClick={() => this.setState({ selectedBucket: selectedBucket === bucket.startDistance ? null : bucket.startDistance })}
                        >
                          <div className="bucket-range">{bucket.startDistance}–{bucket.startDistance + 9} yd</div>
                          <div className="bucket-metric">
                            <div className="bucket-metric-label">
                              <span>Avg tries</span>
                              <strong>{bucket.attemptsAverage.toFixed(1)}</strong>
                            </div>
                            <div className="bucket-meter"><span className="tries-meter" style={{ width: `${(bucket.attemptsAverage / maxBucketAttempts) * 100}%` }} /></div>
                          </div>
                          <div className="bucket-metric">
                            <div className="bucket-metric-label">
                              <span>1st-shot proximity</span>
                              <strong>{bucket.proximityAverage === null ? '—' : `${bucket.proximityAverage.toFixed(1)} yd`}</strong>
                            </div>
                            <div className="bucket-meter"><span className="proximity-meter" style={{ width: `${bucket.proximityAverage === null ? 0 : (bucket.proximityAverage / maxBucketProximity) * 100}%` }} /></div>
                            <small>{bucket.proximityCount} of {bucket.roundCount} rounds recorded</small>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedBucket !== null && (
                    <div className="bucket-trend-detail">
                      <div className="bucket-trend-header">
                        <div>
                          <h4>{selectedBucket}–{selectedBucket + 9} yd historical trend</h4>
                          <p>Daily averages for this target bucket{chartStartDate || chartEndDate ? ' within the selected dates' : ''}.</p>
                        </div>
                        <button type="button" className="btn-close-bucket-trend" onClick={() => this.setState({ selectedBucket: null })} aria-label="Close bucket trend">×</button>
                      </div>
                      {(() => {
                        const bucketDaily = this.getBucketDailyStats(selectedBucket);
                        return (
                          <div className="bucket-trend-grid">
                            {this.renderBucketTrendMetric(
                              bucketDaily.map(day => ({ date: day.date, average: day.attemptsAverage })),
                              'Average tries to succeed',
                              '#27ae60',
                              'tries'
                            )}
                            {this.renderBucketTrendMetric(
                              bucketDaily.filter(day => day.proximityAverage !== null).map(day => ({ date: day.date, average: day.proximityAverage as number })),
                              'Average first-shot proximity',
                              '#3498db',
                              'yd'
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  <p className="chart-hint">{filteredAttempts.length} round{filteredAttempts.length !== 1 ? 's' : ''} included. Proximity averages include only rounds where a first-shot value was recorded.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Golf/DistancePractice.tsx